import React, { useEffect } from "react";

import { AlertTriangle, ArrowRight } from "lucide-react";

import { avatarColor, initials } from "./DisconnectTriggerModal";
import Button from "../../Components/Button";
import CommonModal from "../../Components/Modal";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import { fetchOrganizationLevels } from "../../Store/Reducers/AdminSlice";
import {
  DisconnectReviewAccount,
  DisconnectSponsorAssessment,
} from "../../Store/Reducers/AssessmentSlice";
import { formatAssessmentDateTimeUtc } from "../../Utils/Helper/assessmentDateUtils";
import {
  extractLevelName,
  getAccountLevelCompactLabel,
} from "../../Utils/Helper/levelUtils";

type OrganizationLevel = {
  level_code: string;
  name: string;
};

const LEVEL_BADGE_CLASSES: Record<number, string> = {
  0: "bg-slate-100 text-slate-700",
  1: "bg-red-50 text-red-700",
  2: "bg-orange-50 text-orange-700",
  3: "bg-yellow-50 text-yellow-800",
  4: "bg-sky-50 text-sky-700",
  5: "bg-emerald-50 text-emerald-700",
  6: "bg-violet-50 text-violet-700",
  7: "bg-indigo-50 text-indigo-700",
};

function findOrgLevel(
  levelNum: number,
  organizationLevels: OrganizationLevel[]
): OrganizationLevel | undefined {
  return organizationLevels.find(item => {
    const codeNum = Number(String(item.level_code).replace(/^L/i, ""));
    return Number.isFinite(codeNum) && codeNum === levelNum;
  });
}

function apiLevelDisplay(
  levelNum: number | null,
  levelLabel: string | null | undefined,
  organizationLevels: OrganizationLevel[]
): string {
  if (levelNum == null) {
    return levelLabel?.trim() || "Not submitted";
  }
  const orgLevel = findOrgLevel(levelNum, organizationLevels);
  return getAccountLevelCompactLabel(
    levelNum,
    orgLevel?.name ?? extractLevelName(levelLabel)
  );
}

function SponsorCard({
  sponsor,
  organizationLevels,
}: {
  sponsor: DisconnectSponsorAssessment;
  organizationLevels: OrganizationLevel[];
}) {
  const levelNum =
    typeof sponsor.level === "number" && Number.isFinite(sponsor.level)
      ? sponsor.level
      : null;
  const badgeClass =
    levelNum == null
      ? "bg-gray-100 text-gray-500"
      : (LEVEL_BADGE_CLASSES[levelNum] ?? "bg-gray-100 text-gray-700");

  return (
    <div className="rounded-lg border border-gray-200 p-3">
      <div className="flex items-start gap-2.5">
        <div
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white"
          style={{ backgroundColor: avatarColor(sponsor.name) }}
        >
          {initials(sponsor.name)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="text-sm font-semibold text-gray-900">
                {sponsor.name}
              </div>
              <div className="text-xs text-gray-500">
                {sponsor.title || sponsor.role || "Executive Sponsor"}
              </div>
            </div>
            <span
              className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-semibold ${badgeClass}`}
            >
              {apiLevelDisplay(
                levelNum,
                sponsor.levelLabel,
                organizationLevels
              )}
            </span>
          </div>
          {sponsor.rationale && (
            <p className="mb-0 mt-2 border-t border-gray-100 pt-2 text-xs leading-relaxed text-gray-600">
              {sponsor.rationale}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export interface ResolutionReviewModalProps {
  isOpen: boolean;
  reviewRow: DisconnectReviewAccount | null;
  assessmentTitle?: string;
  isTeamAssessment?: boolean;
  isSubmitting?: boolean;
  onClose: () => void;
  onAccept: () => void;
}

export function readResolutionAgreedLevel(
  reviewRow: DisconnectReviewAccount
): number | null {
  const resolution = reviewRow.resolution as
    | Record<string, unknown>
    | null
    | undefined;
  if (!resolution) return null;
  const raw = resolution.agreed_level ?? resolution.agreedLevel;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw === "string" && raw.trim()) {
    const match = raw.match(/-?\d+/);
    return match ? Number(match[0]) : null;
  }
  return null;
}

function readResolvedByName(reviewRow: DisconnectReviewAccount): string | null {
  return (
    reviewRow.resolvedByName ?? reviewRow.resolution?.resolvedByName ?? null
  );
}

const ResolutionReviewModal: React.FC<ResolutionReviewModalProps> = ({
  isOpen,
  reviewRow,
  assessmentTitle,
  isSubmitting = false,
  onClose,
  onAccept,
}) => {
  const dispatch = useAppDispatch();
  const organizationLevels = useAppSelector(
    state => state.admin.organizationLevels
  ) as OrganizationLevel[];

  useEffect(() => {
    if (isOpen && organizationLevels.length === 0) {
      dispatch(fetchOrganizationLevels());
    }
  }, [dispatch, isOpen, organizationLevels.length]);

  if (!reviewRow) return null;

  const resolution = reviewRow.resolution;
  const agreedLevel = readResolutionAgreedLevel(reviewRow);
  const originalLevel = reviewRow.eslFinalLevel;
  const sponsors = reviewRow.sponsorAssessments ?? [];
  const title = assessmentTitle || reviewRow.assessmentTitle || "Assessment";
  const resolvedAtLabel = resolution?.resolved_at
    ? formatAssessmentDateTimeUtc(resolution.resolved_at)
    : "—";
  const resolvedByName = readResolvedByName(reviewRow);

  const proposedLabel =
    agreedLevel != null
      ? apiLevelDisplay(agreedLevel, `Level ${agreedLevel}`, organizationLevels)
      : "—";
  const originalLabel = apiLevelDisplay(
    originalLevel,
    `Level ${originalLevel}`,
    organizationLevels
  );

  const sponsorNames = sponsors
    .map(s => s.name)
    .filter(Boolean)
    .join(", ");

  return (
    <CommonModal
      isOpen={isOpen}
      toggle={onClose}
      size="5xl"
      title={`Resolution Review: ${title}`}
      contentClassName="overflow-y-auto max-h-[70vh]"
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <Button
            variant="outline-primary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={onAccept}
            disabled={isSubmitting}
            className="inline-flex items-center gap-1.5"
          >
            {isSubmitting ? "Publishing…" : "Force Publish"}
            {!isSubmitting && <ArrowRight className="h-4 w-4" size={16} />}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="mb-0 -mt-1 text-sm leading-relaxed text-gray-500">
          The Primary Sponsor has recorded a resolution. It publishes
          automatically the day after the resolution window closes, or use{" "}
          <strong>Force Publish</strong> to publish it now.
        </p>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto_1fr] md:items-stretch">
          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
            <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-red-700">
              Original ESL Final Level
            </div>
            <div className="text-lg font-bold text-red-900">
              {originalLabel}
            </div>
            <div className="mt-1 text-xs text-red-700">
              Before disconnect resolution
            </div>
          </div>
          <div className="hidden items-center justify-center text-gray-400 md:flex">
            →
          </div>
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
            <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-emerald-700">
              Proposed Resolution Level
            </div>
            <div className="text-lg font-bold text-emerald-900">
              {proposedLabel}
            </div>
            <div className="mt-1 text-xs text-emerald-700">
              Recorded by the Primary Sponsor
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-4">
          <div className="mb-3 text-[11px] font-bold uppercase tracking-wide text-violet-700">
            Agreed Resolution
          </div>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex-1">
              <span className="mb-3 inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-semibold text-yellow-800">
                {proposedLabel}
              </span>
              {resolution?.resolution_justification && (
                <div className="mt-2 border-l-4 border-violet-400 pl-3 text-sm leading-relaxed text-gray-700">
                  {resolution.resolution_justification}
                </div>
              )}
            </div>
            <div className="shrink-0 text-right text-xs text-gray-600">
              <div>
                Resolved by{" "}
                <span className="font-semibold text-gray-900">
                  {resolvedByName || "—"}
                </span>
              </div>
              <div className="mt-1 text-gray-500">{resolvedAtLabel}</div>
            </div>
          </div>
          {sponsorNames && (
            <p className="mb-0 mt-3 text-xs text-gray-500">
              Participants notified: {sponsorNames} • All ExecOps users
            </p>
          )}
        </div>

        <div>
          <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-gray-500">
            Original Sponsor Assessments
          </div>
          <div className="mb-3 flex flex-wrap gap-x-4 gap-y-1 text-xs font-medium text-gray-600">
            <span>
              Min:{" "}
              {apiLevelDisplay(
                reviewRow.minLevel,
                `Level ${reviewRow.minLevel}`,
                organizationLevels
              )}
            </span>
            <span>
              Median:{" "}
              {apiLevelDisplay(
                reviewRow.medianLevel,
                `Level ${reviewRow.medianLevel}`,
                organizationLevels
              )}
            </span>
            <span>
              Max:{" "}
              {apiLevelDisplay(
                reviewRow.maxLevel,
                `Level ${reviewRow.maxLevel}`,
                organizationLevels
              )}
            </span>
          </div>
          <div className="flex flex-col gap-2">
            {sponsors.map((sponsor, index) => (
              <SponsorCard
                key={`${sponsor.executiveId || sponsor.name}-${index}`}
                sponsor={sponsor}
                organizationLevels={organizationLevels}
              />
            ))}
          </div>
        </div>

        <div className="flex items-start gap-2 rounded-lg border border-violet-200 bg-violet-50 p-3.5">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-violet-700" />
          <p className="mb-0 text-sm text-violet-900">
            Force Publish sets the published level to{" "}
            <strong>{proposedLabel}</strong> for <strong>{title}</strong>{" "}
            immediately.
          </p>
        </div>
      </div>
    </CommonModal>
  );
};

export default ResolutionReviewModal;
